<template>
  <div :class="{ open, disabled, light }" class="dropdown" @keydown="keydown">
    <div
      class="list-head" @click.stop="calculatorInputDropdownCombination ? '' : openDropdown()"
    >
      {{ !selectedOption ? selectedTitle : '' }}
      <span v-if="selectedMultiple" :class="{ colorize: selectedMultiple }" v-html="selectedMultiple" />
      <span v-else-if="!htmlRadio" :class="{ colorize: selectedTitle }">{{ selectedOption }}</span>
      <span v-else :class="{ colorize: selectedTitle }" v-html="selectedOption" />
      <component
        :is="arrows"
        @click.stop="calculatorInputDropdownCombination ? (() => {
          openDropdown(), $emit('arrowClicked')
        })() : openDropdown()"
        @mouseover="mouseOverOrLeave('arrowHover')"
        @mouseleave="mouseOverOrLeave('arrowLeave')"
      />
    </div>
    <div v-if="search && open" class="search">
      <input
        id="search"
        v-model="searchKeyword"
        :placeholder="searchPlaceholder"
        class="form-control search-input"
        type="text" autocomplete="off"
        @keyup="searching"
      >
    </div>
    <div
      v-if="open" ref="list" :class="{ 'has-search': search }"
      class="list"
    >
      <li
        v-for="(radio, key, c) in radioButtonsOptions" ref="options" :key="key"
        class="radio" :class="{ selected: selected === key, hovered: arrowCounter === c + 1 && !calculator }"
      >
        <div>
          <input
            :id="key"
            v-model="appliedRadio"
            class="custom-checkbox"
            type="radio"
            :value="key"
            @click="appliedRadio === key ? open = false : null"
            @change="$emit('change', appliedRadio), appliedCheckboxes = [], open = false, searchKeyword = ''"
          >
          <label :title="radio" :class="{ active: selected === key }" :for="key">
            <span v-if="!htmlRadio">
              {{ radio }}
            </span>
            <span v-else v-html="radio" />
          </label>
        </div>
      </li>
      <li
        v-for="(box, key, c) in checkboxOptions" ref="options2" :key="key"
        class="box" :class="{ selected: appliedCheckboxes.includes(key), hovered: radioButtons ? arrowCounter === c + 1 + Object.values(radioButtons).length : arrowCounter === c + 1 }"
      >
        <div>
          <input
            :id="key"
            v-model="appliedCheckboxes"
            class="custom-checkbox"
            type="checkbox"
            :value="key"
            @change="$emit('change', appliedCheckboxes), appliedRadio = '', searchKeyword = '', $emit('selected', { key, checked: appliedCheckboxes.includes(key), all: appliedCheckboxes })"
          >
          <label :title="box" :class="{ active: appliedCheckboxes.includes(key) }" :for="key">
            <span>{{ box }}</span>
          </label>
        </div>
      </li>
      <li v-if="!hasOptions" class="no-results">
        No results for "{{ searchKeyword }}"
      </li>
    </div>
  </div>
</template>

<script>
import Arrow from '../assets/svg/curved-arrow.svg?vue-component';
import CalculatorArrow from '../assets/svg/calculator-arrow.svg?vue-component';
import SearchIcon from '../assets/svg/icon-search.svg?vue-component';

// const isEnglish = (charCode) => (charCode >= 97 && charCode <= 122) || (charCode >= 65 && charCode <= 90);
const englishAlphabet = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z'];

export default {
  components: {
    Arrow,
    CalculatorArrow,
    SearchIcon,
  },
  props: {
    // eslint-disable-next-line vue/require-default-prop
    radioButtons: { type: [Object, Array] },
    // eslint-disable-next-line vue/require-default-prop
    checkBoxes: { type: [Object, Array] },
    selected: { type: String, default: '' },
    selectedTitle: { type: String, default: '' },
    selectedMultiple: { type: String, default: '' },
    selectedCheckboxes: { type: Array, default: () => ([]) },
    searchPlaceholder: { type: String, default: 'Search' },
    disabled: Boolean,
    search: Boolean,
    filtrateSearches: Boolean,
    light: Boolean,
    calculator: Boolean,
    calculatorInputDropdownCombination: Boolean,
    calculatorDecimalPlaces: Boolean,
    reset: Boolean,
    htmlRadio: Boolean,
    additionalSearchData: { type: [Object], required: false, default: () => ({}) },
    noScrollOnSearch: Boolean,
    synced: Boolean,
  },
  data() {
    return {
      appliedCheckboxes: this.selectedCheckboxes,
      appliedRadio: '',
      open: false,
      keysPressed: '',
      arrowCounter: 0,
      navigateKey: '',
      timerID: null,
      timeoutDuration: 1000,
      searchKeyword: '',
    };
  },
  computed: {
    selectedOption() {
      if (this.radioButtons && this.radioButtons[this.selected]) return this.radioButtons[this.selected];
      if (this.checkBoxes && this.checkBoxes[this.selected]) return this.checkBoxes[this.selected];
      return '';
    },
    arrows() {
      return this.calculator ? CalculatorArrow : Arrow;
    },
    hasOptions() {
      const checkboxes = Object.keys(this.checkboxOptions || {});
      const radioButtons = Object.keys(this.radioButtonsOptions || {});

      return checkboxes.length || radioButtons.length;
    },
    checkboxOptions() {
      if (this.searchKeyword && this.checkBoxes) {
        return Object.entries(this.checkBoxes)
          .filter(([k, v]) => v.toLowerCase().includes(this.searchKeyword.toLowerCase()) || (this.additionalSearchData[k] && this.additionalSearchData[k].toLowerCase().includes(this.searchKeyword.toLowerCase())))
          .reduce((p, [k, v]) => ({ ...p, [k]: v }), {});
      }

      return this.checkBoxes;
    },
    radioButtonsOptions() {
      if (this.searchKeyword && this.radioButtons) {
        return Object.entries(this.radioButtons)
          .filter(([k, v]) => (v.toLowerCase().includes(this.searchKeyword.toLowerCase()) || (this.additionalSearchData[k] && this.additionalSearchData[k].toLowerCase().includes(this.searchKeyword.toLowerCase()))))
          .reduce((p, [k, v]) => ({ ...p, [k]: v }), {});
      }

      return this.radioButtons;
    },
  },
  watch: {
    selectedCheckboxes(v) {
      this.appliedCheckboxes = v;
    },
    selected(v) {
      if (this.calculatorInputDropdownCombination) {
        if (['90%', '95%', '99%'].includes(v) || ['0.01', '0.05', '0.10'].includes(v)) {
          this.appliedRadio = v;
        } else {
          this.appliedRadio = '';
        }
      } else if (this.synced || this.calculator) {
        this.appliedRadio = v;
      }

      if (this.calculator) {
        this.$emit('dropdownClosed');
      }
    },
  },
  mounted() {
    if (this.calculator) {
      this.appliedRadio = this.selected;
    }
  },
  methods: {
    searching($event) {
      if (this.filtrateSearches) {
        this.$emit('search', $event.target.value);
      } else {
        this.scrollToElement($event, $event.target.value);
      }
    },
    openDropdown() {
      this.searchKeyword = '';
      document.addEventListener('click', this.close);
      document.addEventListener('keydown', this.keyPressedHandler);
      if (!this.disabled) {
        this.open = !this.open;
        if (this.selected || this.appliedCheckboxes.length) {
          let id = this.selected;
          if (this.appliedCheckboxes.length) {
            id = this.appliedCheckboxes[this.appliedCheckboxes.length - 1];
          }
          this.$nextTick(() => {
            if (document.querySelector(`.list li input[id='${id}']`)) {
              const selected = document.querySelector(`.list li input[id='${id}']`).closest('li');
              if (this.noScrollOnSearch) {
                const parent = selected.parentElement;
                const parentRect = parent.getBoundingClientRect();
                const childRect = selected.getBoundingClientRect();

                parent.scrollTop += (childRect.top - parentRect.top) - (parent.clientHeight / 2) + (childRect.height / 2);
              } else {
                selected.scrollIntoView({ block: this.search ? 'center' : 'nearest', inline: 'nearest' });
              }
              const listItems = document.querySelectorAll('.list li');
              let index;
              // eslint-disable-next-line no-plusplus
              for (let i = 0; i < listItems.length; i++) {
                if (listItems[i] === selected) {
                  index = i;
                }
                if (index) break;
              }
              // this.arrowCounter = index + 1;
            }
          });
        }
      }
      if (this.search) {
        this.$nextTick(() => {
          const searchInput = document.querySelector('.search-input');
          if (searchInput) {
            searchInput.focus();
          }
        });
      }
    },
    close(e) {
      if (!this.$el.contains(e.target)) {
        document.removeEventListener('click', this.close);
        document.removeEventListener('keydown', this.keyPressedHandler);
        this.open = false;
        this.searchKeyword = '';

        this.$emit('dropdownClosed');
      }
    },
    keyPressedHandler(e) {
      if (e.target.classList.contains('search-input')) {
        return;
      }

      if (!englishAlphabet.includes(e.key)) {
        return;
      }

      this.keysPressed += e.key;
      this.navigateKey = e.key;
      this.scrollToElement(e);
      if (this.timerID !== null) {
        clearTimeout(this.timerID);
        this.timerID = null;
      }

      this.timerID = setTimeout(() => {
        this.keysPressed = '';
        this.navigateKey = '';
      }, this.timeoutDuration);
    },
    scrollToElement(e, searchvalue) {
      let search = this.keysPressed.charAt(0).toUpperCase() + this.keysPressed.slice(1).toLowerCase();
      if (searchvalue) {
        this.navigateKey = e.key;
        search = !['ArrowDown', 'ArrowUp', 'Enter'].includes(this.navigateKey) ? searchvalue.charAt(0).toUpperCase() + searchvalue.substring(1) : '';
      }

      const label = document.querySelector(`label[title^='${search}']`);
      const refs = Array.prototype.concat.call(...(this.$refs.options || []), ...(this.$refs.options2 || []));

      if (label) {
        const selected = label.closest('li');
        const listItems = document.querySelectorAll('.list li');
        let index;
        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < listItems.length; i++) {
          if (listItems[i] === selected) {
            index = i;
          }
          if (index) break;
        }
        this.arrowCounter = index + 1;
      }

      if (this.navigateKey === 'ArrowDown') {
        e.preventDefault();
        if (this.arrowCounter < refs.length) {
          this.arrowCounter += 1;
          refs[this.arrowCounter - 1].scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
      } else if (this.navigateKey === 'ArrowUp') {
        e.preventDefault();
        if (this.arrowCounter > 1) {
          this.arrowCounter -= 1;
          refs[this.arrowCounter - 1].scrollIntoView({ block: this.search ? 'center' : 'nearest', inline: 'nearest' });
        }
      } else if (this.navigateKey === 'Enter') {
        e.preventDefault();
        const element = refs[this.arrowCounter - 1].querySelector('input');
        if (element.type === 'checkbox') {
          element.checked = !element.checked;
        }
        const event = new Event('change');
        element.dispatchEvent(event);

        if (element.type === 'radio') {
          this.open = false;
        }
      }

      if (!label) {
        return;
      }

      const list = document.querySelector('.list');
      list.scrollTop = this.search ? (label.offsetTop - 46) : label.offsetTop;
    },
    mouseOverOrLeave(event) {
      if (this.calculatorInputDropdownCombination) {
        if (event === 'arrowHover') {
          this.$emit('arrowHover');
        } else {
          this.$emit('arrowLeave');
        }
      }
    },
    keydown(e) {
      if (e.key === 'Escape') {
        this.searchKeyword = '';
        this.open = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.dropdown {
  box-sizing: border-box;
  cursor: pointer;
  color: var(--main-light-shade-4);
  position: relative;
  width: 208px;
  border: 0;
  height: 40px;
  background: var(--main-light);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  font-weight: 600;
  font-size: 14px;
  line-height: 27px;

  .list-head {
    padding: 8px 16px;
    max-width: 90%;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: var(--main-light-shade-4);

    span {
      width: 100%;
      display: inline-block;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    }

    .colorize {
      color: var(--main-dark-shade-0);
    }

    svg {
      position: absolute;
      right: 8px;
      width: 10px;
      height: 6px;
    }
  }

  .search {
    display: flex;
    align-items: center;
    padding-left: var(--spacing-16);
    padding-right: 0;
    margin-top: -1px;
    z-index: 1;
    background: var(--main-light);
    width: 100%;
    max-width: 90%;
    position: absolute;
    top: 3px;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;

    svg {
      flex: 0 0 auto;
    }

    .search-input {
      font-size: 14px;
      border: 0;
      padding: var(--spacing-8) 0;
      border-radius: 0;

      &::placeholder {
        font-size: 14px;
      }
    }
  }

  .list {
    background: var(--main-light);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
    border-radius: 5px;
    position: absolute;
    z-index: 2;
    height: auto;
    overflow: auto;
    left: 0;
    max-height: 160px;
    right: 0;
    width: 100%;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background-color: var(--main-light);
    }

    &::-webkit-scrollbar-thumb {
      background-color: var(--main-light-shade-3);
      border-radius: 4px;
    }

    li {
      margin: 0;
      display: flex;

      &:hover,
      &.hovered,
      &.selected {
        &:not(.no-results) {
          cursor: pointer;
          background-color: var(--accent-light-2);

          div label span {
            color: var(--accent-shade-2);
            font-weight: 600;
          }
        }

        &.no-results {
          cursor: default;
        }
      }

      a {
        font-weight: 500;
        font-size: 14px;
        line-height: 14px;
        color: var(--black);
        padding: 12px;
        width: 100%;
        overflow: hidden;

        &.selected {
          color: var(--brand);
        }
      }

      div {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        margin: 0 12px;
        width: 100%;

        .custom-checkbox {
          display: none;
        }

        .custom-checkbox + label {
          position: relative;
          font-weight: 500 !important;
          font-size: 14px;
          padding: 12px 0 12px 28px;
          width: 100%;
          color: var(--black);
          cursor: pointer;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          display: flex;

          span:not(.cube) {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            width: 100%;
          }

          .cube {
            height: 18px;
            width: 18px;
            display: block;
            margin: 0 auto;
          }
        }

        label.active {
          font-weight: 600 !important;
          color: var(--accent);
        }

        .custom-checkbox + label:before {
          content: "";
          display: block;
          width: 18px;
          height: 18px;
          border: 1px solid var(--main-light-shade-4);
          background: var(--main-light);
          border-radius: 2px;
          position: absolute;
          left: 0;
          top: 50%;
          margin-top: -10px;
          -webkit-transition: all 0.12s, border-color 0.08s;
          transition: all 0.12s, border-color 0.08s;
        }

        .custom-checkbox + label:after {
          height: 20px;
          width: 20px;
          border-radius: 2px;
        }

        .custom-checkbox:checked + label:after {
          content: "";
          border: 1px solid var(--accent);
          position: absolute;
          left: 0;
          width: 20px;
          height: 20px;
          top: 50%;
          margin-top: -10px;
          bottom: 0;
          box-sizing: border-box;
          border-radius: 2px;
          background: var(--true-white);
          z-index: 1;
        }

        .custom-checkbox:checked + label:before {
          content: "";
          position: absolute;
          left: 6px;
          top: 50%;
          width: 5px;
          margin-top: -8px;
          border-right: 2px solid var(--accent);
          height: 10px;
          border-bottom: 2px solid var(--accent);
          transform: rotate(45deg);
          -webkit-transform: rotate(45deg);
          border-top-color: transparent;
          border-left-color: transparent;
          border-radius: 0;
          z-index: 2;
        }
      }

      .custom-checkbox + label {
        display: flex;
        align-items: center;
        width: 100%;
      }

      &.radio {
        div .custom-checkbox + label {
          padding-left: 0;
        }

        div .custom-checkbox + label:before,
        div .custom-checkbox + label:after {
          display: none;
        }
      }

      &.no-results {
        font-weight: 400;
        padding: var(--spacing-8) var(--spacing-12);
        color: var(--main-dark-light-4);
      }
    }
  }

  &.open {
    border-bottom-right-radius: 0;
    border-bottom-left-radius: 0;

    .list {
      top: 99.9%;
      border-top-right-radius: 0;
      border-top-left-radius: 0;
      box-shadow: 0 8px 15px rgba(0, 0, 0, 0.1);
    }

    .list-head {
      svg {
        transform: rotate(180deg);

        // path[fill] {
        //   fill: var(--accent);
        // }

        // path[stroke] {
        //   stroke: var(--accent);
        // }
      }
    }
  }

  &.light {
    .list-head {
      color: var(--main-light-shade-4);
      font-weight: 400;

      svg {
        path[fill] {
          fill: var(--main-light-shade-4);
        }

        path[stroke] {
          stroke: var(--main-light-shade-4);
        }
      }
    }

    &.open {
      svg {
        path[fill] {
          fill: var(--main-light-shade-4);
        }

        path[stroke] {
          stroke: var(--main-light-shade-4);
        }
      }
    }

    .colorize {
      color: var(--main-dark-shade-0);
      font-weight: 500;
    }
  }

  &.disabled {
    pointer-events: none;
    background: var(--white);
    color: var(--light-gray);

    .list-head svg {
      path[fill] {
        fill: var(--light-gray);
      }

      path[stroke] {
        stroke: var(--light-gray);
      }
    }
  }

  &.products-filter,
  &.revenues-filter {
    .list {
      max-height: unset;
    }
  }
}

.fa {
  .dropdown .list li {
    &:hover,
    &.hovered,
    &.selected {
      background-color: var(--accent-light-1);

      div label span {
        color: var(--accent);
      }
    }
  }
}
</style>
